import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Textarea, Avatar, Badge } from '@mantine/core';
import { useCreateTicketComment } from '@/hooks/api-hooks';
import { TicketComment, Ticket } from '@turizi/shared';
import GlobalSearch from './global-search';
import { TagIcon } from '@/lib/tag-utils';
import { tagColor } from '@/lib/format-utils';
import { IconTrash } from '@tabler/icons-react';
import { BusinessToString, PersonToString, ServiceToString } from '@/lib/models-utils';
import { Link } from 'react-router-dom';
import { BUSINESS_PATH, PERSON_PATH, SERVICE_PATH, RESERVATION_PATH, SETTINGS_USERS_PATH } from '@/lib/navigation';

interface TicketCommentsProps {
    ticket: Ticket;
}

export default function TicketComments({ ticket }: TicketCommentsProps) {
    const { t } = useTranslation();
    const createCommentMutation = useCreateTicketComment();

    const [newComment, setNewComment] = useState('');
    const [taggedResources, setTaggedResources] = useState<any[]>([]);
    const [isFocused, setIsFocused] = useState(false);

    const handleSubmit = async () => {
        if (!newComment.trim() && taggedResources.length === 0) return;

        try {
            await createCommentMutation.mutateAsync({
                ticketId: ticket.id,
                data: {
                    content: newComment,
                    tagged_resources: taggedResources
                }
            });
            setNewComment('');
            setTaggedResources([]);
            setIsFocused(false);
        } catch (error) {
            console.error('Error adding comment:', error);
        }
    };

    const handleTagResource = (item: any) => {
        if (!taggedResources.find(t => t.id === item.id && t.type === item.type)) {
            setTaggedResources([...taggedResources, item]);
        }
    };

    const removeTaggedResource = (index: number) => {
        const updated = [...taggedResources];
        updated.splice(index, 1);
        setTaggedResources(updated);
    };

    const renderTaggedResource = (item: any) => {
        let label = item.name || item.label;
        if (item.type === 'business') label = BusinessToString(item, t);
        if (item.type === 'person') label = PersonToString(item, t);
        if (item.type === 'service') label = ServiceToString(item, t);

        return (
            <div key={`${item.type}-${item.id}`} className="flex items-center gap-2 bg-gray-100 px-2 py-1 rounded-md text-xs border border-gray-200">
                <TagIcon name={item.category || item.type} size="xs" hideText noBackground />
                <span className="font-medium truncate max-w-[150px]">{label}</span>
                <IconTrash
                    size={14}
                    className="text-gray-400 hover:text-red-500 cursor-pointer transition-colors ml-1"
                    onClick={() => removeTaggedResource(taggedResources.indexOf(item))}
                />
            </div>
        );
    };

    const getResourceLink = (item: any) => {
        switch (item.type) {
            case 'business': return BUSINESS_PATH.replace(':id', item.id);
            case 'person': return PERSON_PATH.replace(':id', item.id);
            case 'service': return SERVICE_PATH.replace(':id', item.id);
            case 'reservation': return RESERVATION_PATH.replace(':id', item.id);
            case 'user': return SETTINGS_USERS_PATH;
            default: return '#';
        }
    };

    const renderCommentTaggedResource = (item: any, idx: number) => {
        let label = item.name || item.label;
        if (item.type === 'business') label = BusinessToString(item, t);
        if (item.type === 'person') label = PersonToString(item, t);
        if (item.type === 'service') label = ServiceToString(item, t);

        const link = getResourceLink(item);

        return (
            <Link
                to={link}
                target={link !== '#' ? "_blank" : undefined}
                key={`${item.type}-${item.id}-${idx}`}
                className="flex items-center gap-1.5 bg-blue-50/50 hover:bg-blue-50 px-2 py-1 rounded text-xs border border-blue-100 w-fit mt-2 transition-colors cursor-pointer"
            >
                <TagIcon name={item.category || item.type} size="xxs" hideText noBackground />
                <span className="font-medium text-blue-700">{label}</span>
            </Link>
        );
    };

    return (
        <div className="mt-2">
            <div className="space-y-6">
                {ticket.comments?.map((comment: TicketComment) => (
                    <div key={comment.id} className="flex gap-4">
                        <Avatar color="blue" radius="xl" size="md">
                            {comment.creator?.name?.charAt(0) || comment.creator?.email?.charAt(0) || '?'}
                        </Avatar>
                        <div className="flex-1">
                            <div className="flex items-baseline gap-2">
                                <span className="font-semibold text-sm text-gray-900">
                                    {comment.creator?.name || comment.creator?.email || t('unknown_user')}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {new Date(comment.created_at).toLocaleString()}
                                </span>
                            </div>

                            <div className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">
                                {comment.content}
                            </div>

                            {comment.tagged_resources && comment.tagged_resources.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {comment.tagged_resources.map((res, idx) => renderCommentTaggedResource(res, idx))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {/* {(!ticket.comments || ticket.comments.length === 0) && (
                    <div className="text-center text-gray-400 text-sm py-4">
                        {t('no_comments_yet')}
                    </div>
                )} */}
            </div>

            {/* Comment Input */}
            <div className="flex gap-4">
                <Avatar color="gray" radius="xl" size="md" />
                <div className={`flex-1 transition-all ${isFocused ? 'bg-gray-50' : 'bg-transparent'} rounded-lg border ${isFocused ? 'border-gray-300 shadow-sm' : 'border-gray-200'} p-3`}>
                    <Textarea
                        placeholder={t('add_a_comment')}
                        minRows={2}
                        autosize
                        variant="unstyled"
                        value={newComment}
                        onChange={(e) => setNewComment(e.currentTarget.value)}
                        onFocus={() => setIsFocused(true)}
                        className="w-full"
                    />

                    {taggedResources.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3 mb-2 border-t border-gray-100 pt-3">
                            {taggedResources.map(renderTaggedResource)}
                        </div>
                    )}

                    {(isFocused || newComment || taggedResources.length > 0) && (
                        <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-100">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 font-medium">Tag Resource:</span>
                                <div className="w-48">
                                    <GlobalSearch
                                        onSelect={handleTagResource}
                                        size="xs"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    variant="subtle"
                                    color="gray"
                                    size="xs"
                                    onClick={() => {
                                        setNewComment('');
                                        setTaggedResources([]);
                                        setIsFocused(false);
                                    }}
                                >
                                    {t('cancel')}
                                </Button>
                                <Button
                                    color="black"
                                    size="xs"
                                    onClick={handleSubmit}
                                    loading={createCommentMutation.isPending}
                                    disabled={!newComment.trim() && taggedResources.length === 0}
                                >
                                    {t('comment')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
